import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, MessageCircle, CheckCircle2, Check } from "lucide-react";

function FileField({ value, onChange, field }) {
  const [uploading, setUploading] = useState(false);
  async function up(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "quotes");
    setUploading(true);
    try {
      const { data } = await http.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      toast.success("Archivo subido");
    } catch {
      toast.error("Error al subir");
    } finally {
      setUploading(false);
    }
  }
  return (
    <label className="mt-2 flex items-center gap-3 px-4 py-3 bg-neutral-50 border border-dashed border-black/15 rounded-xl cursor-pointer hover:border-[var(--brand-primary)]">
      <Upload size={18} />
      <span className="text-sm">{uploading ? "Subiendo..." : value ? "Archivo cargado ✓" : field.placeholder || "Adjuntar imagen"}</span>
      <input type="file" accept="image/*" className="hidden" onChange={up} data-testid={`quote-field-${field.id}`} />
    </label>
  );
}

function ChoiceChips({ field, value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (l) => onChange(selected.includes(l) ? selected.filter((x) => x !== l) : [...selected, l]);
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid={`quote-field-${field.id}`}>
      {(field.options || []).map((o) => {
        const on = selected.includes(o.label);
        return (
          <button type="button" key={o.label} onClick={() => toggle(o.label)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 font-semibold" : "border-black/10 bg-white"}`}
            data-testid={`quote-opt-${field.id}-${o.label}`}>
            {o.hex && <span className="h-4 w-4 rounded-full border border-black/10" style={{ background: o.hex }} />}
            {o.label}
            {on && <Check size={12} />}
          </button>
        );
      })}
    </div>
  );
}

function Field({ field, value, onChange }) {
  const tid = `quote-field-${field.id}`;
  switch (field.type) {
    case "textarea":
      return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={field.placeholder} data-testid={tid} />;
    case "select":
      return (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={tid}><SelectValue placeholder={field.placeholder || "Selecciona"} /></SelectTrigger>
          <SelectContent>{(field.options || []).map((o) => <SelectItem key={o.label} value={o.label}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      );
    case "multiselect":
    case "colors":
      return <ChoiceChips field={field} value={value} onChange={onChange} />;
    case "file":
      return <FileField field={field} value={value} onChange={onChange} />;
    default:
      return <Input type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} data-testid={tid} />;
  }
}

const FULL = ["textarea", "file", "multiselect", "colors"];
const display = (v) => (Array.isArray(v) ? v.join(", ") : v);

export default function Quote() {
  const { settings } = useStore();
  const [fields, setFields] = useState([]);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    http.get("/quote-form").then(({ data }) => setFields((data.fields || []).filter((f) => f.active !== false)));
  }, []);

  const set = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  async function submit() {
    const missing = fields.filter((f) => f.required && !(Array.isArray(answers[f.id]) ? answers[f.id].length : answers[f.id]));
    if (missing.length) {
      toast.error(`Completa: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }
    setSending(true);
    try {
      await http.post("/quotes", { name: answers.name, whatsapp: answers.whatsapp, answers });
      setDone(true);
      toast.success("Cotización enviada");
    } catch {
      toast.error("Error al enviar");
    } finally {
      setSending(false);
    }
  }

  function waLink() {
    if (!settings?.whatsapp_number) return "#";
    const wa = settings.whatsapp_number.replace(/\D/g, "");
    const lines = [
      "*Cotización personalizada - Floral Rossé*",
      "",
      ...fields.filter((f) => f.type !== "file" && answers[f.id] && display(answers[f.id])).map((f) => `${f.label}: ${display(answers[f.id])}`),
    ];
    return `https://wa.me/${wa}?text=${encodeURIComponent(lines.join("\n"))}`;
  }

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16" data-testid="quote-success">
        <div className="bg-white rounded-3xl p-10 border border-black/5 text-center">
          <CheckCircle2 size={60} className="text-[var(--brand-accent)] mx-auto" />
          <h1 className="font-serif text-4xl font-bold mt-6">¡Cotización enviada!</h1>
          <p className="mt-4 text-neutral-600">{settings?.msg_quote_confirmation || "Te contactaremos pronto. Puedes también escribirnos directamente por WhatsApp."}</p>
          <a href={waLink()} target="_blank" rel="noreferrer">
            <Button className="mt-6 btn-accent rounded-full h-14 px-8 font-semibold" data-testid="quote-wa-btn">
              <MessageCircle size={18} className="mr-2" /> Enviar por WhatsApp
            </Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="quote-page">
      <div className="text-xs uppercase tracking-widest text-[var(--brand-accent)] font-bold">B2B · Proyectos</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-bold mt-2">Cotización personalizada</h1>
      <p className="mt-3 text-neutral-600 max-w-2xl">
        Arreglos y estructuras a medida para eventos, salones y proyectos de decoración. Completa el formulario y nos pondremos en contacto.
      </p>

      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-black/5 mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.id} className={f.width === "full" || FULL.includes(f.type) ? "sm:col-span-2" : ""}>
            <Label>{f.label}{f.required && <span className="text-[var(--brand-primary)]"> *</span>}</Label>
            <Field field={f} value={answers[f.id]} onChange={(v) => set(f.id, v)} />
          </div>
        ))}
        <div className="sm:col-span-2 flex justify-end">
          <Button onClick={submit} disabled={sending || !fields.length} className="btn-primary rounded-full h-12 px-8 font-semibold" data-testid="quote-submit">
            {sending ? "Enviando..." : "Enviar cotización"}
          </Button>
        </div>
      </div>
    </div>
  );
}
